import { motion, type Variants } from 'framer-motion';

/**
 * VaultDoor — the hero illustration on the welcome screen.
 *
 * Three animation states, driven by the `state` prop:
 *   - 'idle'         : wheel slowly rotates, bolts extended, door closed
 *   - 'authenticating' : wheel spins faster, bolts retracting, slight pulse
 *   - 'unlocked'     : door halves split outward, bolts fully retracted,
 *                      wheel stops at "open" angle
 *
 * SVG is hand-built (no raster), with named groups for each animatable
 * piece. Pure CSS gradients give the brushed-metal look without images.
 */

export type VaultDoorState = 'idle' | 'authenticating' | 'unlocked';

interface VaultDoorProps {
  state: VaultDoorState;
  /** Side length in px. Defaults to 360. */
  size?: number;
  className?: string;
}

/* -- Animation variants --------------------------------------------- */

const wheelIdleRotate: Variants = {
  idle: { rotate: 0, transition: { duration: 60, ease: 'linear', repeat: Infinity } },
  authenticating: {
    rotate: 360,
    transition: { duration: 0.9, ease: [0.4, 0, 0.2, 1], repeat: Infinity },
  },
  unlocked: { rotate: 90, transition: { duration: 0.6, ease: [0.34, 1.56, 0.64, 1] } },
};

const boltRetract: Variants = {
  idle: { scale: 1, opacity: 1 },
  authenticating: {
    scale: 0.85,
    opacity: 0.7,
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
  },
  unlocked: {
    scale: 0.6,
    opacity: 0.4,
    transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1] },
  },
};

const doorHalfTop: Variants = {
  idle: { y: 0 },
  authenticating: { y: -3, transition: { duration: 0.4 } },
  unlocked: {
    y: -58,
    transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] },
  },
};

const doorHalfBottom: Variants = {
  idle: { y: 0 },
  authenticating: { y: 3, transition: { duration: 0.4 } },
  unlocked: {
    y: 58,
    transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] },
  },
};

const glowPulse: Variants = {
  idle: { opacity: 0 },
  authenticating: {
    opacity: [0.2, 0.5, 0.2],
    transition: { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
  },
  unlocked: {
    opacity: 0.7,
    transition: { duration: 0.5 },
  },
};

const ticksRotate: Variants = {
  idle: { rotate: 0, transition: { duration: 120, ease: 'linear', repeat: Infinity } },
  authenticating: {
    rotate: 360,
    transition: { duration: 6, ease: 'linear', repeat: Infinity },
  },
  unlocked: { rotate: 45, transition: { duration: 0.6 } },
};

/* -- Defs (gradients, filters) ------------------------------------- */

function Defs() {
  return (
    <defs>
      {/* Outer frame — dark brushed metal */}
      <linearGradient id="vd-frame" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3a3f47" />
        <stop offset="50%" stopColor="#1d2025" />
        <stop offset="100%" stopColor="#0f1114" />
      </linearGradient>

      {/* Inner door face — slightly lighter steel */}
      <radialGradient id="vd-face" cx="40%" cy="35%" r="80%">
        <stop offset="0%" stopColor="#2c3038" />
        <stop offset="55%" stopColor="#1a1d22" />
        <stop offset="100%" stopColor="#0a0b0e" />
      </radialGradient>

      {/* Wheel — brighter accent highlight */}
      <radialGradient id="vd-wheel" cx="40%" cy="35%" r="70%">
        <stop offset="0%" stopColor="#3a414b" />
        <stop offset="60%" stopColor="#22262d" />
        <stop offset="100%" stopColor="#101216" />
      </radialGradient>

      {/* Bolt head */}
      <radialGradient id="vd-bolt" cx="40%" cy="40%" r="60%">
        <stop offset="0%" stopColor="#4a4f57" />
        <stop offset="100%" stopColor="#1a1d22" />
      </radialGradient>

      {/* Accent ring highlight */}
      <linearGradient id="vd-accent" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#FF8A64" />
        <stop offset="100%" stopColor="#FF6A3D" />
      </linearGradient>

      {/* Vault inner glow (revealed on unlock) */}
      <radialGradient id="vd-inside" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FF8A64" stopOpacity="0.85" />
        <stop offset="60%" stopColor="#FF6A3D" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#FF6A3D" stopOpacity="0" />
      </radialGradient>

      {/* Path for arched VAULT text — top half of outer frame */}
      <path
        id="vd-arc"
        d="M 80,200 A 120,120 0 0,1 320,200"
        fill="none"
      />

      {/* Subtle noise overlay */}
      <filter id="vd-noise">
        <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" />
        <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0" />
      </filter>
    </defs>
  );
}

/* -- Component ----------------------------------------------------- */

export function VaultDoor({ state, size = 360, className }: VaultDoorProps) {
  return (
    <motion.svg
      viewBox="0 0 400 400"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="Vault door"
      initial={false}
      animate={state}
    >
      <Defs />

      {/* === Outer frame (stays put) === */}
      <g>
        {/* Outermost ring with subtle accent rim */}
        <circle cx="200" cy="200" r="195" fill="url(#vd-frame)" />
        <circle
          cx="200"
          cy="200"
          r="190"
          fill="none"
          stroke="url(#vd-accent)"
          strokeWidth="1.2"
          strokeOpacity="0.35"
        />
        {/* Tick marks at compass points */}
        <motion.g animate={state} variants={ticksRotate} style={{ transformOrigin: '200px 200px' }}>
          {Array.from({ length: 36 }).map((_, i) => {
            const angle = (i * 10 * Math.PI) / 180;
            const r1 = 184;
            const r2 = i % 9 === 0 ? 174 : 178;
            const x1 = 200 + Math.cos(angle) * r1;
            const y1 = 200 + Math.sin(angle) * r1;
            const x2 = 200 + Math.cos(angle) * r2;
            const y2 = 200 + Math.sin(angle) * r2;
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={i % 9 === 0 ? '#FF8A64' : '#3a3f47'}
                strokeWidth={i % 9 === 0 ? 1.2 : 0.6}
                strokeLinecap="round"
                strokeOpacity={i % 9 === 0 ? 0.7 : 0.4}
              />
            );
          })}
        </motion.g>
        {/* Arched VAULT label */}
        <text
          fill="#E8E9EB"
          fillOpacity="0.65"
          fontFamily="Instrument Serif, Georgia, serif"
          fontSize="22"
          fontStyle="italic"
          letterSpacing="6"
        >
          <textPath href="#vd-arc" startOffset="50%" textAnchor="middle">
            VAULT
          </textPath>
        </text>
      </g>

      {/* === Inner area === */}
      <g>
        {/* Inner ring bezel */}
        <circle cx="200" cy="200" r="170" fill="#0a0b0e" />
        <circle
          cx="200"
          cy="200"
          r="170"
          fill="none"
          stroke="#2c3038"
          strokeWidth="2"
        />
        <circle
          cx="200"
          cy="200"
          r="166"
          fill="none"
          stroke="#1a1d22"
          strokeWidth="1"
        />

        {/* === Inside glow (revealed on unlock) === */}
        <motion.circle
          cx="200"
          cy="200"
          r="160"
          fill="url(#vd-inside)"
          variants={glowPulse}
          style={{ mixBlendMode: 'screen' }}
        />

        {/* === Door top half === */}
        <motion.g variants={doorHalfTop}>
          <clipPath id="vd-clip-top">
            <rect x="0" y="0" width="400" height="200" />
          </clipPath>
          <g clipPath="url(#vd-clip-top)">
            <circle cx="200" cy="200" r="155" fill="url(#vd-face)" />
            {/* Subtle highlight arc */}
            <path
              d="M 60,170 A 155,155 0 0,1 340,170"
              fill="none"
              stroke="#3a3f47"
              strokeWidth="1"
              strokeOpacity="0.6"
            />
          </g>
        </motion.g>

        {/* === Door bottom half === */}
        <motion.g variants={doorHalfBottom}>
          <clipPath id="vd-clip-bottom">
            <rect x="0" y="200" width="400" height="200" />
          </clipPath>
          <g clipPath="url(#vd-clip-bottom)">
            <circle cx="200" cy="200" r="155" fill="url(#vd-face)" />
            <path
              d="M 60,230 A 155,155 0 0,0 340,230"
              fill="none"
              stroke="#0a0b0e"
              strokeWidth="2"
              strokeOpacity="0.6"
            />
          </g>
        </motion.g>

        {/* === Door edge shadow (subtle, stays with frame) === */}
        <circle
          cx="200"
          cy="200"
          r="155"
          fill="none"
          stroke="#0a0b0e"
          strokeWidth="2"
          strokeOpacity="0.5"
        />

        {/* === Locking bolts — 4 cardinal positions === */}
        {[
          { x: 200, y: 50 }, // top
          { x: 350, y: 200 }, // right
          { x: 200, y: 350 }, // bottom
          { x: 50, y: 200 }, // left
        ].map((pos, i) => (
          <motion.g
            key={i}
            variants={boltRetract}
            style={{ transformOrigin: `${pos.x}px ${pos.y}px` }}
          >
            <circle cx={pos.x} cy={pos.y} r="14" fill="url(#vd-bolt)" />
            <circle
              cx={pos.x}
              cy={pos.y}
              r="14"
              fill="none"
              stroke="#0a0b0e"
              strokeWidth="1.5"
            />
            <circle cx={pos.x} cy={pos.y} r="4" fill="#0a0b0e" />
          </motion.g>
        ))}

        {/* === Central wheel === */}
        <motion.g
          variants={wheelIdleRotate}
          style={{ transformOrigin: '200px 200px' }}
        >
          {/* Wheel base */}
          <circle cx="200" cy="200" r="78" fill="url(#vd-wheel)" />
          <circle
            cx="200"
            cy="200"
            r="78"
            fill="none"
            stroke="#0a0b0e"
            strokeWidth="2"
          />
          <circle
            cx="200"
            cy="200"
            r="72"
            fill="none"
            stroke="#3a3f47"
            strokeWidth="0.5"
            strokeOpacity="0.5"
          />
          {/* 8 spokes */}
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i * 45 * Math.PI) / 180;
            const x1 = 200 + Math.cos(angle) * 20;
            const y1 = 200 + Math.sin(angle) * 20;
            const x2 = 200 + Math.cos(angle) * 70;
            const y2 = 200 + Math.sin(angle) * 70;
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#0a0b0e"
                strokeWidth="3"
                strokeLinecap="round"
              />
            );
          })}
          {/* Spoke caps (knobs) */}
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i * 45 * Math.PI) / 180;
            const x = 200 + Math.cos(angle) * 70;
            const y = 200 + Math.sin(angle) * 70;
            return (
              <g key={`cap-${i}`}>
                <circle cx={x} cy={y} r="6" fill="#1a1d22" />
                <circle cx={x} cy={y} r="6" fill="none" stroke="#3a3f47" strokeWidth="0.8" />
              </g>
            );
          })}
          {/* Center hub */}
          <circle cx="200" cy="200" r="20" fill="#0a0b0e" />
          <circle cx="200" cy="200" r="20" fill="none" stroke="#FF6A3D" strokeWidth="1.5" strokeOpacity="0.7" />
          <circle cx="200" cy="200" r="8" fill="url(#vd-bolt)" />
        </motion.g>

        {/* Subtle noise overlay on the whole door */}
        <circle cx="200" cy="200" r="195" fill="#ffffff" filter="url(#vd-noise)" opacity="0.5" style={{ mixBlendMode: 'overlay' }} />
      </g>
    </motion.svg>
  );
}